import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { Button } from '@optools/components/ui/button';
import { UserSession } from '@optools/services/operator-gateway/user-lookup/types';
import { formatFullDateAndTimeUTC, formatFullDateUtc } from '@optools/utils/datetime';
import { ColumnDef, ExpandedState, PaginationState, Updater } from '@tanstack/react-table';
import { ChevronUp } from 'lucide-react';
import { useMemo, useState } from 'react';

import { QueryParams } from '../schemas';
import { ExpandingDataTable } from './expanding-data-table';
import { PlaybackSessionsSheet } from './playback-sessions-sheet';
import { SessionDetails } from './session-details';

export interface SessionsTableProps {
  onUpdateSearchParams: (params: Partial<QueryParams>) => void;
  pagination: PaginationState;
  selectedSessionId?: string;
  sessions: UserSession[];
  total: number;
}

export function SessionsTable({
  onUpdateSearchParams,
  pagination,
  selectedSessionId,
  sessions,
  total,
}: SessionsTableProps) {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onUpdateSearchParams({ sessionId: '' });
    }
  };

  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    const { pageIndex, pageSize } = updater instanceof Function ? updater(pagination) : updater;
    onUpdateSearchParams({ page: pageIndex, size: pageSize });
    setExpanded({});
  };

  const userSessionColumns: ColumnDef<UserSession>[] = useMemo(
    () => [
      {
        accessorKey: 'sessionId',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Session ID" />,
        id: 'Session ID',
      },
      {
        accessorFn: (row) => {
          return formatFullDateAndTimeUTC(row.initTime.replace('[UTC]', ''));
        },
        accessorKey: 'initTime',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Init Time" />,
        id: 'Init Time',
      },
      {
        accessorFn: (row) => {
          return formatFullDateUtc(row.expireDate.replace('[UTC]', ''));
        },
        accessorKey: 'expireDate',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Expired Date" />,
        id: 'Expired Date',
      },
      {
        accessorFn: (row) => {
          return row.features.join(', ');
        },
        accessorKey: 'features',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Features" />,
        id: 'Features',
      },
      {
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <div className="flex justify-end gap-6">
              {row.original.playbackSessions.length > 0 && (
                <Button
                  size="sm"
                  onClick={() => onUpdateSearchParams({ sessionId: row.original.sessionId })}>
                  Playback Sessions
                </Button>
              )}
              <Button
                aria-label="Expand Details"
                className="group"
                data-state={row.getIsExpanded() ? 'open' : 'closed'}
                size="icon"
                variant="ghost"
                onClick={row.getToggleExpandedHandler()}>
                <ChevronUp className="group-data-[state='closed']:rotate-180" />
              </Button>
            </div>
          ) : (
            ''
          );
        },
        id: 'details',
      },
    ],
    [onUpdateSearchParams],
  );

  const selectedPlaybackSessions = useMemo(
    () =>
      sessions?.find((session) => session.sessionId === selectedSessionId)?.playbackSessions ?? [],
    [selectedSessionId, sessions],
  );

  return (
    <>
      <ExpandingDataTable
        columns={userSessionColumns}
        data={sessions}
        enableRowSelection={false}
        manualPagination={true}
        pagination={true}
        renderExpandedRow={(rowData) => <SessionDetails session={rowData} />}
        rowCount={total}
        search={false}
        state={{ expanded, pagination }}
        onExpandedChange={setExpanded}
        onPaginationChange={(state) => handlePaginationChange(state)}>
        <h2 className="text-sm font-bold">User Sessions</h2>
      </ExpandingDataTable>
      <PlaybackSessionsSheet
        open={!!selectedPlaybackSessions.length}
        playbackSessions={selectedPlaybackSessions}
        onOpenChange={handleOpenChange}
      />
    </>
  );
}
